import DraggableContainer from '@components/system/DraggableContainer/DraggableContainer';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormHelperText,
  ListItemText,
  MenuItem,
  Select,
  TextField
} from '@material-ui/core';
import React, {useEffect, useRef, useState} from 'react';
import {useStyles} from './DraggableCCMCardStyle';
import {useDispatch, useSelector} from 'react-redux';
import Timer from '../TimeTracker/Timer';
import RecordDetailsDialog from '../CCMRecordDetails/RecordDetailsDialog';
import {useTranslation} from 'react-i18next';
import CCMCreatePlanDialog from '../CCMCreatePlanDialog/CCMCreatePlanDialog';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import MessagesConversationDialog from '../../../Sms/components/MessagesConversationDialog/MessagesConversationDialog';
import {useSnackbar} from 'notistack';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import {Permission} from '@shared/services/PermissionService';
import {
  AppPermissions,
  CCMConstants,
  FOLLOW_UP_NOTES_LOG_STATUS,
  PatientsTabKeys,
  SEARCH_DEBOUNCE_DELAY
} from '@shared/constants/Mapping';
import {
  _patientDrawerDetails,
  _setActiveTab
} from '@shared/store/actions/PatientDrawerAction';
import {CommunicationService} from 'src/service/CommunicationService';
import Storage from 'src/utils/Storage';
import {useDebouncedCallback} from 'use-debounce';
import {
  _updateFollowUpNotes,
  _updateStopTimerNotes,
  _updateFollowUpDueDateAndAssignee,
  _ccmResetTimer
} from '@shared/store/actions/CCMTimerActions';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import {
  cancelCCMTimerPatientLog,
  getAllCCMUsersOfClinicGroup,
  updatePrevFollowUpNotesLog
} from '@shared/services/CCMServices';
import {Controller, useForm} from 'react-hook-form';
import DateTimePickerWrapper from '@components/system/CustomDateTimePicker/DateTimePickerWrapper';
import moment from 'moment';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import HelperUtils from '@shared/utils/HelperUtils';
import CCMShowFullNotesDialog from '../CCMShowFullNotesDialog/CCMShowFullNotesDialog';
import {State} from '@shared/store/reducers/Index';
import DeleteAlert from '../../../Patients/DeleteAlert/DeleteAlert';

interface CCMPanelData {
  groupId: number;
  clinicId: number;
  patientId: number;
  ccmPatientId: number;
  guarantorId: number;
  isPlanActive?: boolean;
  patientName: string;
  patientAccountNumber: string;
  prevFollowUpNotes: string;
  prevFollowUpNotesLogId: number;
  isExternalPlanActive: boolean;
}

const DraggableCCMCard = ({
  groupId,
  initialCcmPanelData
}: {
  groupId: number;
  initialCcmPanelData?: CCMPanelData;
}): JSX.Element => {
  const classes = useStyles();
  const [ccmPanelData, setCCMPanelData] = useState<null | CCMPanelData>(
    initialCcmPanelData ?? null
  );
  const [dialogState, setDialogState] = useState({
    shouldShowRecordDetails: false,
    shouldShowCreatePlanDialog: false,
    editPlan: false,
    moreDetails: false
  });

  const [conversationDialogState, setConversationDialogState] = useState<{
    shouldShowConversationDialog: boolean;
    messageId: number | null;
  }>({
    shouldShowConversationDialog: false,
    messageId: null
  });

  const cardRef = useRef<any>();

  const updatePanelData = () => {
    const timerDataStr = Storage.getItem(CCMConstants.STORAGE_CCM_TIMER_DATA);
    if (timerDataStr) {
      const parsedTimerData = JSON.parse(timerDataStr);
      if (parsedTimerData && parsedTimerData?.ccmPanelData) {
        setCCMPanelData(parsedTimerData?.ccmPanelData);
      }
    }
  };

  const [selectedAssignee, setSelectedAssignee] = React.useState(''); // To manage selected assignee
  const [ccmUsers, setCcmUsers] = useState([]);
  const {control, errors, setValue, watch} = useForm({
    mode: 'all',
    resolver: async (values: any) => {
      const error: any = {};
      if (!values?.follow_up_notes_due_date) {
        error.follow_up_notes_due_date = 'CCM.STOP_TIMER.DUE_DATE_IS_REQUIRED';
      } else if (!moment(values?.follow_up_notes_due_date).isValid()) {
        error.follow_up_notes_due_date = 'CCM.STOP_TIMER.INVALID_DATE';
      } else if (
        moment(values?.follow_up_notes_due_date).isBefore(
          moment().startOf('day')
        )
      ) {
        error.follow_up_notes_due_date = 'CCM.STOP_TIMER.INVALID_DUE_DATE';
      }
      if (!values?.follow_up_notes_assignee) {
        error.follow_up_notes_assignee = 'CCM.STOP_TIMER.ASSIGNEE_IS_REQUIRED';
      }
      return {
        values,
        errors: error
      };
    },
    defaultValues: {
      follow_up_notes_due_date: null
    }
  });

  useEffect(() => {
    updatePanelData();
  }, []);

  const {shouldShowRecordDetails, shouldShowCreatePlanDialog} = dialogState;
  let localStorageData: any = Storage.getItem(
    CCMConstants.STORAGE_CCM_TIMER_DATA
  );
  if (localStorageData) {
    localStorageData = JSON?.parse?.(localStorageData);
  }

  useEffect(() => {
    const eventSubscription = CommunicationService.getEvent().subscribe(
      event => {
        if (event.action === 'CCM_PANEL_DATA_UPDATED') {
          // read from the local storage and update the local state
          updatePanelData();
        }
      }
    );
    return () => eventSubscription.unsubscribe();
  }, []);

  const openRecordDetailsDialog = () => {
    setDialogState(p => ({
      ...p,
      shouldShowRecordDetails: true
    }));
  };

  const closeRecordDetailsDialog = () => {
    setDialogState(p => ({
      ...p,
      shouldShowRecordDetails: false
    }));
  };
  const dispatch = useDispatch();
  const userPermissions = useSelector(
    (state: State) => state.auth.userPermissions
  );

  const isPatientDetailsDrawerOpen = useSelector(
    (state: any) => state.patientDetailsDrawer.open
  );

  const timerInfo = useSelector((stateParam: any) => stateParam.ccmTimer);

  const notesUpdateDebounced = useDebouncedCallback(notes => {
    dispatch(_updateStopTimerNotes(notes));
  }, SEARCH_DEBOUNCE_DELAY);

  const followUpNotesUpdateDebounced = useDebouncedCallback(followUpNotes => {
    dispatch(_updateFollowUpNotes(followUpNotes));
  }, SEARCH_DEBOUNCE_DELAY);

  const openPatientDrawer = () => {
    // Will we not check user permission
    // TODO: Check Permission
    Permission.hasPatientPermissions(
      userPermissions,
      AppPermissions.PATIENT.READ,
      groupId
    );
    if (ccmPanelData) {
      if (isPatientDetailsDrawerOpen) {
        dispatch(_setActiveTab(PatientsTabKeys.CCM));
      } else {
        dispatch(
          _patientDrawerDetails(
            ccmPanelData?.groupId,
            ccmPanelData.clinicId,
            ccmPanelData?.patientId,
            PatientsTabKeys.CCM
          )
        );
      }
    }
  };

  const openCreatePlanDialog = () => {
    setDialogState(p => ({
      ...p,
      shouldShowCreatePlanDialog: true,
      editPlan: ccmPanelData?.isPlanActive ?? false
    }));
  };

  const closeCreatePlanDialog = () => {
    setDialogState(p => ({
      ...p,
      shouldShowCreatePlanDialog: false
    }));
  };

  const [inputValues, setInputValues] = useState({
    notes: '',
    followUpNotes: ''
  });

  const notesTextChangeHandler = (notesData: string) => {
    setInputValues(p => ({
      ...p,
      notes: notesData
    }));
    notesUpdateDebounced(notesData);
  };
  const followUpNotesTextChangeHandler = (followUpNotesData: string) => {
    setInputValues(p => ({
      ...p,
      followUpNotes: followUpNotesData
    }));
    followUpNotesUpdateDebounced(followUpNotesData);
  };

  useEffect(() => {
    setInputValues(p => ({
      ...p,
      notes: timerInfo?.stopTimerNotes
    }));
  }, [timerInfo?.stopTimerNotes]);

  useEffect(() => {
    setInputValues(p => ({
      ...p,
      followUpNotes: timerInfo?.followUpNotes
    }));
  }, [timerInfo?.followUpNotes]);

  useEffect(() => {
    setSelectedAssignee(
      timerInfo?.followUpDueDateAndAssigneeObj?.followUpAssignee?.label || ''
    );
    setValue(
      'follow_up_notes_assignee',
      timerInfo?.followUpDueDateAndAssigneeObj?.followUpAssignee?.id || null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerInfo?.followUpDueDateAndAssigneeObj?.followUpAssignee]);

  useEffect(() => {
    setValue(
      'follow_up_notes_due_date',
      timerInfo?.followUpDueDateAndAssigneeObj?.followUpDueDate || null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerInfo?.followUpDueDateAndAssigneeObj?.followUpDueDate]);

  const {enqueueSnackbar} = useSnackbar();

  const {t} = useTranslation();
  const [
    showCancelCCMTimerDialog,
    setShowCancelCCMTimerDialog
  ] = useState<boolean>(false);

  const onSendMessageClick = async (): Promise<void> => {
    try {
      if (ccmPanelData) {
        if (isPatientDetailsDrawerOpen) {
          dispatch(_setActiveTab(PatientsTabKeys.MESSAGES));
        } else {
          dispatch(
            _patientDrawerDetails(
              ccmPanelData?.groupId,
              ccmPanelData.clinicId,
              ccmPanelData?.patientId,
              PatientsTabKeys.MESSAGES
            )
          );
        }
      }
    } catch (err: any) {
      enqueueSnackbar(err && err.message, SnackBarConfig('e'));
      // throw err;
    }
  };

  const handleConversationClose = () => {
    setConversationDialogState(p => ({
      ...p,
      shouldShowConversationDialog: false,
      messageId: null
    }));
  };

  const markPrevFollowUpNotesAsDone = async () => {
    try {
      if (ccmPanelData) {
        const payload = {
          status: FOLLOW_UP_NOTES_LOG_STATUS.COMPLETED
        };
        await updatePrevFollowUpNotesLog(
          ccmPanelData?.groupId,
          ccmPanelData?.ccmPatientId,
          ccmPanelData?.prevFollowUpNotesLogId,
          payload
        );
        updatePanelData();
        const timerDataStr = Storage.getItem(
          CCMConstants.STORAGE_CCM_TIMER_DATA
        );
        if (timerDataStr) {
          const parsedTimerData = JSON.parse(timerDataStr);
          if (parsedTimerData && parsedTimerData?.ccmPanelData) {
            Storage.setItem(
              CCMConstants.STORAGE_CCM_TIMER_DATA,
              JSON.stringify({
                ...parsedTimerData,
                ccmPanelData: {
                  ...parsedTimerData.ccmPanelData,
                  prevFollowUpNotes: '',
                  prevFollowUpNotesLogId: undefined
                }
              })
            );
          }
        }
        updatePanelData();
        CommunicationService.sendEvent({
          action: 'MARK_PREVIOUS_FOLLOW_UP_NOTES_AS_DONE',
          data: {
            patientId: ccmPanelData.patientId,
            clinicId: ccmPanelData.clinicId
          }
        });
      }
    } catch (err: any) {
      enqueueSnackbar(err && err.message, SnackBarConfig('e'));
    }
  };

  const renderPrevFollowUpNotes = () => {
    const prevFollowUpNotes = ccmPanelData?.prevFollowUpNotes || '';
    const maxCharacters = 15;
    return (
      <div className={`${classes.prevFollowUpNotesWrapper}`}>
        <p className={`${classes.ccmPatientDetailsHeader}`}>
          {t('CCM.PREVIOUS_FOLLOW_UP_NOTES')}
        </p>
        <div className={`${classes.followUpNotesAndMarkAsDoneWrapper}`}>
          {prevFollowUpNotes.length <= maxCharacters ? (
            <p>{ccmPanelData?.prevFollowUpNotes}</p>
          ) : (
            <p>
              {prevFollowUpNotes.slice(0, maxCharacters)}
              {'... '}
              <span
                onClick={() => {
                  setDialogState(p => ({
                    ...p,
                    moreDetails: true
                  }));
                }}
                className="link-text-style"
                style={{cursor: 'pointer'}}>
                {t('CCM.MORE_DETAILS')}
              </span>
            </p>
          )}
          <div
            onClick={markPrevFollowUpNotesAsDone}
            className={`link-text-style ${classes.markFollowUpNoteAsDoneWrapper}`}>
            <CheckCircleOutlineIcon />
            <p>{t('CCM.MARK_PREV_FOLLOW_UP_NOTE_AS_DONE')}</p>
          </div>
        </div>
      </div>
    );
  };

  // TODO: Replace this with the redux value once the patient shared redux state issue is fixed
  let patientShared = false;
  if (groupId) {
    patientShared =
      userPermissions?.[groupId]?.['group']?.[
        AppPermissions.PATIENT_MENU_PERMISSIONS.PATIENT_SHARED
      ] === true;
  }
  const fetchCCMUsersList = async () => {
    if (patientShared === false && !ccmPanelData?.clinicId) {
      return;
    }
    try {
      // eslint-disable-next-line camelcase
      const options: {clinic_id?: number | string} = {};
      if (!patientShared) {
        options['clinic_id'] = ccmPanelData?.clinicId;
      }
      const response = await getAllCCMUsersOfClinicGroup(groupId, options);
      setCcmUsers(response?.data?.rows);
    } catch (error: any) {
      enqueueSnackbar(
        (error && error.message) || t('CLINICS.CLINIC_GROUP_USER_ERROR'),
        SnackBarConfig('e')
      );
    }
  };

  useEffect(() => {
    fetchCCMUsersList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ccmPanelData?.clinicId]);

  const renderFollowUpNotesDueDate = () => {
    return (
      <div>
        <p className={classes.notesLabel}>
          {t('CCM.FOLLOW_UP_NOTES_COLUMN.FOLLOW_UP_NOTES_DUE_DATE')}
        </p>
        <FormControl
          style={{marginBottom: 0}}
          fullWidth={true}
          variant="outlined"
          error={!!errors?.follow_up_notes_due_date}>
          <Controller
            control={control}
            error={!!errors?.follow_up_notes_due_date}
            name="follow_up_notes_due_date"
            render={({onChange, ...inputProps}) => (
              <DateTimePickerWrapper
                style={{width: '100%'}}
                minDate={new Date()}
                autoComplete="on"
                margin={'dense'}
                type={'string'}
                placeholder={t('CCM.DUE_DATE_PLACEHOLDER')}
                {...inputProps}
                onChange={(date, value) => {
                  setValue('follow_up_notes_due_date', value, {
                    shouldValidate: true
                  });
                  dispatch(
                    _updateFollowUpDueDateAndAssignee({
                      followUpDueDate: value as string
                    })
                  );
                }}
              />
            )}
            defaultValue={null}
          />
          {errors?.follow_up_notes_due_date && (
            <FormHelperText>
              {t(errors.follow_up_notes_due_date)}
            </FormHelperText>
          )}
        </FormControl>
      </div>
    );
  };
  const handleSelectAssignee = (item: any) => {
    setSelectedAssignee(
      HelperUtils.commaSeparatedFullName(item?.first_name, item?.last_name)
    );
  };
  const hasAssignee = !!watch('follow_up_notes_assignee');
  useEffect(() => {
    if (!hasAssignee) {
      setSelectedAssignee('');
    }
  }, [hasAssignee]);

  const renderFollowUpNotesAssignee = () => {
    return (
      <div>
        <p className={classes.notesLabel}>
          {t('CCM.FOLLOW_UP_NOTES_COLUMN.FOLLOW_UP_NOTES_ASSIGNEE')}
        </p>
        <FormControl
          style={{marginBottom: 0}}
          fullWidth={true}
          error={!!errors?.follow_up_notes_assignee}
          variant="outlined"
          className="select_clinic">
          <Controller
            id="follow_up_notes_assignee"
            control={control}
            name="follow_up_notes_assignee"
            error={!!errors?.follow_up_notes_assignee}
            render={({onBlur}) => (
              <Select
                labelId="demo-multiple-name-label"
                id="demo-multiple-name"
                margin="dense"
                value={selectedAssignee || 'Select Assignee'} // Set selected value in the dropdown
                variant="outlined"
                renderValue={selected => selected as string}
                fullWidth={true}
                onBlur={onBlur}
                name="follow_up_notes_assignee"
                IconComponent={KeyboardArrowDownIcon}>
                <div>
                  {ccmUsers.map((item: any) => (
                    <MenuItem
                      key={item?.id}
                      value={HelperUtils.commaSeparatedFullName(
                        item?.first_name,
                        item?.last_name
                      )}
                      onClick={() => {
                        handleSelectAssignee(item);
                        setValue('follow_up_notes_assignee', item.id, {
                          shouldValidate: true
                        });
                        dispatch(
                          _updateFollowUpDueDateAndAssignee({
                            followUpAssignee: {
                              id: item?.id,
                              label: HelperUtils.commaSeparatedFullName(
                                item?.first_name,
                                item?.last_name
                              )
                            }
                          })
                        );
                      }}>
                      <ListItemText
                        primary={HelperUtils.commaSeparatedFullName(
                          item?.first_name,
                          item?.last_name
                        )}
                      />
                    </MenuItem>
                  ))}
                </div>
              </Select>
            )}
            defaultValue={null}
          />
          {errors?.follow_up_notes_assignee && (
            <FormHelperText>
              {t(errors.follow_up_notes_assignee)}
            </FormHelperText>
          )}
        </FormControl>
      </div>
    );
  };
  const renderFollowUpNotesDueDateAndAssignedUserDetails = () => {
    return (
      <div>
        {renderFollowUpNotesDueDate()}
        {renderFollowUpNotesAssignee()}
      </div>
    );
  };

  const renderMoreNotesDetails = () => {
    return (
      <CCMShowFullNotesDialog
        open={dialogState?.moreDetails}
        onClose={() => {
          setDialogState(p => ({
            ...p,
            moreDetails: false
          }));
        }}
        notes={ccmPanelData?.prevFollowUpNotes as string}
        title={t('CCM.PREVIOUS_FOLLOW_UP_NOTES')}
      />
    );
  };
  const handleCloseCancelCCMTimerDialog = async (res: 'confirm' | 'cancel') => {
    if (res === 'confirm') {
      try {
        const options: {clinic_id?: number | string} = {};
        if (!patientShared) {
          options['clinic_id'] = ccmPanelData?.clinicId;
        }
        const response = await cancelCCMTimerPatientLog(
          groupId,
          ccmPanelData?.ccmPatientId,
          options
        );
        if (response) {
          enqueueSnackbar(t(response?.message), SnackBarConfig('s'));
          Storage.setItem(
            CCMConstants.STORAGE_CCM_TIMER_DATA,
            JSON.stringify({
              startTime: null,
              stopTime: null,
              isRunning: false,
              patientId: '',
              ccmPatientId: ''
            })
          );
          dispatch(_ccmResetTimer());
          CommunicationService.sendEvent({
            action: 'STOPPED_CCM_TIMER',
            data: null
          });
        }
      } catch (e: any) {
        enqueueSnackbar(e?.message, SnackBarConfig('e'));
      }
    }
    setShowCancelCCMTimerDialog(false);
  };

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <React.Fragment>
      {ccmPanelData && (
        <>
          <DraggableContainer bounds="parent" handle="#draggable">
            <Card className={classes.ccmFloatCard} ref={cardRef}>
              <CardHeader
                className={classes.ccmFloatCardHeader}
                title={
                  <div
                    id="draggable"
                    className={classes.ccmFloatCardMainHeaderWrapper}>
                    <div className={classes.ccmFloatCardHeaderWrapper}>
                      <DragIndicatorIcon className={classes.ccmPanelDragIcon} />
                      <p>{t('CCM.CCM_FLOAT_PANEL.CCM_TIMER')}</p>
                    </div>
                    <div className={classes.ccmCancelTimer}>
                      <p
                        className={`link-text-style ${classes.link}`}
                        onClick={() => {
                          setShowCancelCCMTimerDialog(true);
                        }}>
                        {t('CCM.CCM_FLOAT_PANEL.CANCEL_CCM_TIMER.BUTTON_LABEL')}
                      </p>
                    </div>
                  </div>
                }
              />
              <CardContent className={classes.cardContent}>
                <div className={classes.ccmTimerWrapper}>
                  <Timer
                    patientId={ccmPanelData?.patientId}
                    ccmPatientId={ccmPanelData?.ccmPatientId}
                    groupId={ccmPanelData?.groupId}
                    clinicId={ccmPanelData?.clinicId}
                    patientName={ccmPanelData?.patientName}
                    patientAccountNumber={ccmPanelData?.patientAccountNumber}
                    isFromFloatingPanel={true}
                  />
                </div>
                <div className={classes.ccmPanelInfoWrapper}>
                  <Card>
                    <CardHeader
                      title={
                        <div className={classes.ccmPatientDetailsHeader}>
                          <p>{t('CCM.CCM_FLOAT_PANEL.PATIENT_DETAILS')}</p>
                          {Permission.hasIncomingSmsPermissions(
                            userPermissions,
                            AppPermissions.INCOMING_SMS.REPLY,
                            groupId
                          ) && (
                            <p
                              className={`link-text-style ${classes.link}`}
                              onClick={onSendMessageClick}>
                              {t('CCM.CCM_FLOAT_PANEL.SEND_MESSAGE')}
                            </p>
                          )}
                        </div>
                      }
                    />
                    <CardContent className={classes.ccmPatientInfoCardContent}>
                      <div className={classes.ccmPatientInfoWrapper}>
                        <p>{t('CCM.CCM_FLOAT_PANEL.NAME')}</p>
                        <p>:</p>
                        <p
                          className={`link-text-style ${classes.link}`}
                          onClick={openPatientDrawer}>
                          {ccmPanelData?.patientName || '-'}
                        </p>
                        <p>{t('CCM.CCM_FLOAT_PANEL.ACCOUNT_NUMBER')}</p>
                        <p>:</p>
                        <p>{ccmPanelData?.patientAccountNumber || '-'}</p>
                      </div>
                      {ccmPanelData?.prevFollowUpNotes &&
                        renderPrevFollowUpNotes()}
                    </CardContent>
                  </Card>
                  <div className={classes.ccmButtonWrapper}>
                    {ccmPanelData?.isPlanActive ? (
                      <>
                        {!ccmPanelData?.isExternalPlanActive && (
                          <Button
                            variant="contained"
                            color="primary"
                            className={classes.recordDetailsButton}
                            onClick={openRecordDetailsDialog}>
                            {t('CCM.CCM_FLOAT_PANEL.RECORD_DETAILS')}
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          color="primary"
                          className={classes.editPlansButton}
                          onClick={openCreatePlanDialog}>
                          {t('CCM.CCM_FLOAT_PANEL.EDIT_PLAN')}
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.createPlanButton}
                        onClick={openCreatePlanDialog}>
                        {t('CCM.CCM_FLOAT_PANEL.CREATE_PLAN')}
                      </Button>
                    )}
                  </div>
                  <div>
                    <p className={classes.notesLabel}>{`${t('CCM.NOTES')}`}</p>
                    <TextField
                      fullWidth={true}
                      className={`app-text-area ${classes.textArea}`}
                      placeholder={t('CCM.ENTER_SOME_NOTES')}
                      onChange={e => notesTextChangeHandler(e.target.value)}
                      value={inputValues.notes}
                      multiline
                      variant="outlined"
                      rows={2}
                    />
                  </div>
                  <div>
                    <p className={classes.notesLabel}>
                      {`${t('CCM.FOLLOW_UP_NOTES')}`}
                    </p>
                    <TextField
                      fullWidth={true}
                      className={`app-text-area ${classes.textArea}`}
                      placeholder={t('CCM.ENTER_FOLLOW_UP_NOTES')}
                      onChange={e =>
                        followUpNotesTextChangeHandler(e.target.value)
                      }
                      value={inputValues.followUpNotes}
                      multiline
                      variant="outlined"
                      rows={2}
                    />
                  </div>
                  <div>
                    {renderFollowUpNotesDueDateAndAssignedUserDetails()}
                  </div>
                </div>
              </CardContent>
            </Card>
          </DraggableContainer>
          {shouldShowRecordDetails && (
            <RecordDetailsDialog
              open={shouldShowRecordDetails}
              onClose={closeRecordDetailsDialog}
              patientName={ccmPanelData?.patientName}
              accountNumber={ccmPanelData?.patientAccountNumber}
              groupId={ccmPanelData?.groupId}
              clinicId={ccmPanelData?.clinicId}
              patientId={ccmPanelData?.patientId}
              ccmPatientId={ccmPanelData?.ccmPatientId}
              isExternalPlanActiveProp={ccmPanelData?.isExternalPlanActive}
            />
          )}

          {shouldShowCreatePlanDialog && (
            <CCMCreatePlanDialog
              open={shouldShowCreatePlanDialog}
              fromEdit={dialogState.editPlan}
              patientName={ccmPanelData?.patientName}
              accountNumber={ccmPanelData?.patientAccountNumber}
              onClose={response => {
                if (response === 'confirm') {
                  closeCreatePlanDialog();
                } else {
                  closeCreatePlanDialog();
                }
              }}
              ccmPatientId={ccmPanelData?.ccmPatientId}
              clinicId={ccmPanelData?.clinicId}
              isExternalPlanActiveProp={ccmPanelData?.isExternalPlanActive}
            />
          )}

          {conversationDialogState.shouldShowConversationDialog &&
            ccmPanelData?.clinicId &&
            ccmPanelData?.guarantorId && (
              <MessagesConversationDialog
                groupId={ccmPanelData?.groupId}
                clinicId={ccmPanelData?.clinicId}
                messageId={conversationDialogState?.messageId as number}
                guarantorId={ccmPanelData?.guarantorId}
                handleClose={handleConversationClose}
                showConversation={
                  conversationDialogState.shouldShowConversationDialog
                }
              />
            )}
          {showCancelCCMTimerDialog && (
            <DeleteAlert
              open={showCancelCCMTimerDialog}
              close={handleCloseCancelCCMTimerDialog}
              title={t('CCM.CCM_FLOAT_PANEL.CANCEL_CCM_TIMER.TITLE')}
              description={t(
                'CCM.CCM_FLOAT_PANEL.CANCEL_CCM_TIMER.DESCRIPTION'
              )}
            />
          )}
          {dialogState?.moreDetails && renderMoreNotesDetails()}
        </>
      )}
    </React.Fragment>
  );
};

export default DraggableCCMCard;
